import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";

import { USER_ROLES } from "../../utils/constants";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import useGetVisitSurveys from "../../hooks/useGetVisitSurveys";
import useUpdateVisitSurveysMutation from "../../hooks/useUpdateVisitSurveysMutation";
import useGetPhotoSessionSurveys from "../../hooks/useGetPhotoSessionSurveys";
import useUpdatePhotoSessionSurveysMutation from "../../hooks/useUpdatePhotoSessionSurveysMutation";

const SurveysModule = ({
  role,
  allSurveys = [],
  selectedSessionId,
  selectedVisit,
  patientId,
  visitId,
  step,
  disabled = false,
  headerless = false,
  manageTestId,
  isTrialLocked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // CRO's superset selection (used in CRO mode)
  const [localSelectedSurveys, setLocalSelectedSurveys] = useState([]);
  const [localDisallowInv, setLocalDisallowInv] = useState(false);
  const [localIsAllSurvey, setLocalIsAllSurvey] = useState(false);

  // Investigator's checked survey IDs (used in patient mode)
  const [invSelectedIds, setInvSelectedIds] = useState([]);

  const sessionId = selectedSessionId || selectedVisit?.photo_session_id;
  const stepId = step?.id;
  const label = step?.title || "POEMs";
  const testIdSuffix = stepId ? `-${stepId}` : "";

  const updateMutation = useUpdateVisitSurveysMutation();
  const sessionMutation = useUpdatePhotoSessionSurveysMutation();

  // CRO superset — always fetched to drive the investigator's checklist and enabled state
  const { data: visitSurveysData } = useGetVisitSurveys({ visitId, stepId });
  const visitSurveys = visitSurveysData?.survey_form || [];

  // Session-level surveys — always fetched in patient mode to drive the enabled checkbox
  const { data: sessionSurveysData } = useGetPhotoSessionSurveys({
    sessionId,
    stepId,
    enabled: !!patientId,
  });

  const isReadOnly =
    isTrialLocked ||
    role === USER_ROLES.REVIEWER ||
    (patientId && visitSurveysData?.disallow_inv_survey_selection);

  // Adverse Event visits must always keep the investigator locked out of picking surveys —
  // CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;

  // CRO mode: sync enabled from visit data
  useEffect(() => {
    if (!patientId && visitSurveys.length) {
      setEnabled(true);
    }
  }, [patientId, visitSurveys.length]);

  // Patient mode: enabled only when the investigator has explicitly assigned surveys to this
  // session — surveys merely inherited from the visit are not a real choice yet. Exception:
  // when the investigator is locked out of picking (disallow_inv), there will never be an
  // explicit session selection, so the visit's forced superset counts as "on" instead.
  useEffect(() => {
    if (
      patientId &&
      (sessionSurveysData?.is_session_selection ||
        visitSurveysData?.disallow_inv_survey_selection) &&
      sessionSurveysData?.survey_form?.length
    ) {
      setEnabled(true);
    }
  }, [patientId, sessionSurveysData, visitSurveysData]);

  // Initialise local state from server data each time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalDisallowInv(isAdverseEventVisit || !!visitSurveysData?.disallow_inv_survey_selection);
      setLocalIsAllSurvey(!!visitSurveysData?.is_all_survey);
      if (!patientId) {
        setLocalSelectedSurveys(visitSurveys);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate the investigator's selection when dialog opens — only when the session has an
  // explicit override; otherwise the visit's surveys are just inherited, not actually chosen yet.
  // Exception: when disallow_inv is set, the visit's forced superset is shown (read-only) instead.
  useEffect(() => {
    if (
      open &&
      patientId &&
      (sessionSurveysData?.is_session_selection ||
        visitSurveysData?.disallow_inv_survey_selection) &&
      sessionSurveysData?.survey_form
    ) {
      setInvSelectedIds(sessionSurveysData.survey_form.map((s) => s.id));
    } else if (open && patientId) {
      setInvSelectedIds([]);
    }
  }, [open, sessionSurveysData, patientId, visitSurveysData]);

  const localSelectedIds = useMemo(
    () => localSelectedSurveys.map((s) => s.id),
    [localSelectedSurveys]
  );

  const allCROSelected = allSurveys.length > 0 && localSelectedIds.length === allSurveys.length;
  const allInvSelected = visitSurveys.length > 0 && invSelectedIds.length === visitSurveys.length;

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);

    if (!checked) {
      if (patientId) {
        setInvSelectedIds([]);
        if (sessionId) {
          sessionMutation.mutate({ sessionId, stepId, surveyIds: [] });
        }
      } else {
        setLocalSelectedSurveys([]);
        updateMutation.mutate({ visitId, stepId, surveyIds: [] });
      }
    }
  };

  const handleDisableInvSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  const toggleSurvey = (survey) => {
    if (isReadOnly) return;

    setLocalSelectedSurveys((prev) => {
      const exists = prev.find((s) => s.id === survey.id);
      return exists ? prev.filter((s) => s.id !== survey.id) : [...prev, survey];
    });
  };

  const handleSelectAllCRO = () => {
    setLocalSelectedSurveys(allCROSelected ? [] : allSurveys);
  };

  const toggleInvSurvey = (id) => {
    setInvSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAllInv = () => {
    setInvSelectedIds(allInvSelected ? [] : visitSurveys.map((s) => s.id));
  };

  const handleSave = () => {
    if (patientId) {
      sessionMutation.mutate(
        { sessionId, stepId, surveyIds: invSelectedIds },
        { onSuccess: () => setOpen(false) }
      );
    } else {
      updateMutation.mutate(
        {
          visitId,
          stepId,
          surveyIds: localSelectedIds,
          disallowInvSurveySelection: headerless ? false : localDisallowInv,
          isAllSurvey: headerless ? undefined : localIsAllSurvey,
        },
        { onSuccess: () => setOpen(false) }
      );
    }
  };

  const canManage = !isReadOnly;

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    [USER_ROLES.INVESTIGATOR, USER_ROLES.SUB_INVESTIGATOR, USER_ROLES.CRC].includes(role) &&
    patientId &&
    !headerless &&
    selectedVisit?.visit_number >= 0 &&
    visitSurveysData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={headerless ? {} : { width: "100%" }}>
      {headerless ? (
        <CustomButton
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          data-testid={manageTestId}
        >
          {canManage ? "Manage" : "View"}
        </CustomButton>
      ) : (
        /* Header */
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={
                patientId
                  ? isReadOnly || !visitSurveys.length || sessionMutation.isPending
                  : isReadOnly || disabled || updateMutation.isPending
              }
              inputProps={{ "data-testid": `poem-enable-checkbox-input${testIdSuffix}` }}
            />

            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
          </Stack>

          {enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              {patientId && (
                <Tooltip title="Go to POEMs">
                  <IconButton
                    component={Link}
                    target="_blank"
                    to={`/patient/${patientId}/poem/${!!selectedSessionId ? selectedSessionId : selectedVisit.photo_session_id}/?visitName=${encodeURIComponent(selectedVisit.visit_name)}`}
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton
                size="small"
                data-testid={`poem-manage-btn${testIdSuffix}`}
                onClick={() => setOpen(true)}
              >
                {canManage ? "Manage" : "View"}
              </CustomButton>
            </Stack>
          )}
        </Stack>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {canManage ? "Manage" : "View"} {label}
        </DialogTitle>

        <DialogContent dividers>
          {/* CRO mode: pick the superset from all available surveys */}
          {!patientId && (
            <>
              {!headerless && !isReadOnly && (
                <Box sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={localDisallowInv}
                      onChange={handleDisableInvSelection}
                      disabled={isAdverseEventVisit}
                    />
                    <Typography variant="body2">
                      Disable survey selection for investigator
                      {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                    </Typography>
                  </Stack>

                  {canSaveForFollowingVisits(selectedVisit) && (
                    <SaveForFollowingVisitsCheckbox
                      label="Save surveys configurations for all coming visits"
                      checked={localIsAllSurvey}
                      onChange={() => setLocalIsAllSurvey((prev) => !prev)}
                    />
                  )}
                </Box>
              )}

              {!isReadOnly && (
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
                  onClick={handleSelectAllCRO}
                >
                  {allCROSelected ? "Deselect All Surveys: " : "Select All Surveys: "}
                  {localSelectedIds.length}
                </Typography>
              )}

              <List>
                {allSurveys.map((survey) => {
                  const checked = localSelectedIds.includes(survey.id);

                  return (
                    <ListItem key={survey.id} disableGutters sx={{ mb: 1 }}>
                      <Paper
                        sx={{
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSurvey(survey)}
                          disabled={isReadOnly}
                        />
                        <ListItemText primary={survey.name} />
                      </Paper>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          {/* Patient / investigator mode: subset selection from the CRO superset */}
          {patientId && (
            <>
              {!isReadOnly && (
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: "primary.main", mb: 1 }}
                  onClick={handleSelectAllInv}
                >
                  {allInvSelected ? "Deselect All Surveys: " : "Select All Surveys: "}
                  {invSelectedIds.length}
                </Typography>
              )}

              <List>
                {visitSurveys.map((survey) => (
                  <ListItem key={survey.id} disableGutters sx={{ mb: 1 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox
                        checked={invSelectedIds.includes(survey.id)}
                        disabled={isReadOnly}
                        onChange={() => toggleInvSurvey(survey.id)}
                      />
                      <ListItemText primary={survey.name} />
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {canManage && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={updateMutation.isPending || sessionMutation.isPending}
            >
              {updateMutation.isPending || sessionMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveysModule;
